import { useMemo } from 'react';
import { Box, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {
  SInfiniteTable,
  SNavigateButton,
  SRoundedContainer,
  SSearchInput,
  SText,
  TableLoader
} from '@setvi/shared/components';
import type { ModifiableProduct } from 'pages/sales/modules/orders/subpages/orders/subpages/order-create';
import { buildOrderHistoryColumns } from 'pages/quotes-v2/components/add-by-order-history/components/columns';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';
import {
  useShipToOrderHistory,
  type UseShipToOrderHistoryProps
} from '../hooks/use-ship-to-order-history';
import { ProductsEmptyState } from '../../../components';
import { ContentTitleContainer } from '../../product-info';

interface ShipToOrderHistoryProps extends UseShipToOrderHistoryProps {
  productName: string;
  customerName?: string;
  onBack: () => void;
  onClose?: () => void;
}

export const ShipToOrderHistory = ({
  productName,
  customerName,
  onBack,
  onClose,
  ...queryProps
}: ShipToOrderHistoryProps) => {
  const {
    products,
    search,
    setSearch,
    isLoading,
    isFetchingMore,
    fetchMore,
    onStockRefresh
  } = useShipToOrderHistory(queryProps);

  const columns = useMemo(
    () =>
      buildOrderHistoryColumns({
        selectedProducts: [],
        onProductChange: () => {},
        onStockRefresh
      }).filter(col => col.id !== 'quantity'),
    [onStockRefresh]
  );

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Box display="flex" alignItems="center" gridGap={8} mb={1}>
        <SNavigateButton onClick={onBack} size="sm" />
        <SText
          title={
            customerName
              ? `Products bought by ${toTitleCase(customerName)}`
              : productName
          }
          weight="semibold"
          maxLines={1}
          size="xl"
        />
        {onClose && (
          <Box ml="auto">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon style={{ width: 27, height: 27 }} />
            </IconButton>
          </Box>
        )}
      </Box>

      <SRoundedContainer
        style={{
          flex: 1,
          overflow: 'hidden auto',
          margin: 8,
          display: 'flex',
          flexDirection: 'column'
        }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb="12px">
          <ContentTitleContainer>
            <SText title="All Products" weight="semibold" />
          </ContentTitleContainer>
          <SSearchInput
            placeholder="Search by Product Name, SKU..."
            searchedValue={search}
            onChange={setSearch}
            style={{ backgroundColor: 'white' }}
          />
        </Box>

        <Box flex={1} overflow="auto" display="flex" flexDirection="column">
          {!isLoading && !products.length ? (
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center">
              <ProductsEmptyState title="No Ship To Order History" />
            </Box>
          ) : (
            <>
              <SInfiniteTable<ModifiableProduct>
                list={products}
                columns={columns}
                searchQuery={search}
                header={{}}
                infiniteScroll={{ fetchMore }}
                keyGetter={item => item?._id}
              />
              {(isLoading || isFetchingMore) && (
                <TableLoader columns={columns} />
              )}
            </>
          )}
        </Box>
      </SRoundedContainer>
    </Box>
  );
};
